import { Button, Dialog } from '@protoss/ui';
import * as React from 'react';

import './confirm-delete-dialog.css';

export interface ConfirmDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Heading, e.g. "Delete deal?" */
  title: string;
  /**
   * Text the user must type to enable the destructive button - the entity's
   * name/title for named things, or an uppercase verb like "DELETE" for
   * unnamed actions. Comparison is trimmed; an empty keyword never matches.
   */
  confirmKeyword: string;
  /** Optional context rendered above the "Type X to confirm" line. */
  description?: React.ReactNode;
  /** Destructive button label. Default "Delete". */
  confirmLabel?: string;
  /** Destructive button label while the action runs. Default "Deleting…". */
  pendingLabel?: string;
  /** While true, inputs and dismissal are disabled and the pending label shows. */
  isPending?: boolean;
  /**
   * 'exact' (default) compares the typed text verbatim; 'caseInsensitive'
   * lowercases both sides (used by bulk deletes that ask the user to type "yes").
   */
  matchMode?: 'exact' | 'caseInsensitive';
  onConfirm: () => void | Promise<void>;
}

/**
 * Shared type-to-confirm deletion modal. The destructive button stays disabled
 * until the user types {@link ConfirmDeleteDialogProps.confirmKeyword} (trimmed,
 * per `matchMode`). Centralizes the confirm-input state, Enter-to-submit,
 * reset-on-toggle, and pending gating that were previously copy-pasted across
 * the app's individual delete dialogs.
 */
export function ConfirmDeleteDialog({
  open,
  onOpenChange,
  title,
  confirmKeyword,
  description,
  confirmLabel = 'Delete',
  pendingLabel = 'Deleting…',
  isPending = false,
  matchMode = 'exact',
  onConfirm,
}: ConfirmDeleteDialogProps) {
  const [confirmInput, setConfirmInput] = React.useState('');

  // Clear the typed value whenever the dialog opens or closes so a prior
  // attempt never carries over into the next one.
  React.useEffect(() => {
    setConfirmInput('');
  }, [open]);

  const normalize = (value: string) =>
    matchMode === 'caseInsensitive' ? value.trim().toLowerCase() : value.trim();
  const confirmMatch =
    confirmKeyword !== '' && normalize(confirmInput) === normalize(confirmKeyword);

  const handleConfirm = React.useCallback(() => {
    if (!confirmMatch || isPending) return;
    void onConfirm();
  }, [confirmMatch, isPending, onConfirm]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (isPending) return;
        onOpenChange(next);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="confirm-delete-dialog__overlay" />
        <Dialog.Content className="confirm-delete-dialog__content">
          <div className="confirm-delete-dialog__header">
            <Dialog.Title className="confirm-delete-dialog__title">{title}</Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="confirm-delete-dialog__close"
                aria-label="Close"
                disabled={isPending}
              >
                ×
              </button>
            </Dialog.Close>
          </div>
          <div className="confirm-delete-dialog__body">
            {description != null && (
              <div className="confirm-delete-dialog__description">{description}</div>
            )}
            <Dialog.Description className="confirm-delete-dialog__prompt">
              Type <strong className="confirm-delete-dialog__keyword">{confirmKeyword}</strong> to
              confirm.
            </Dialog.Description>
            <input
              className="confirm-delete-dialog__input"
              type="text"
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder={confirmKeyword}
              autoFocus
              disabled={isPending}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleConfirm();
                }
              }}
            />
          </div>
          <div className="confirm-delete-dialog__actions">
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="sm" disabled={isPending}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={!confirmMatch || isPending}
              onClick={handleConfirm}
            >
              {isPending ? pendingLabel : confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
